<template>
  <div class="dashboard-layout">
    <!-- Modern Sidebar -->
    <AppSidebar v-model:collapsed="sidebarCollapsed" :mobile-open="mobileNavOpen" />

    <!-- DEV-2232: на узком экране сайдбар выезжает поверх контента, под ним затемнение -->
    <Transition name="fade">
      <div v-if="mobileNavOpen" class="dashboard-layout__backdrop" @click="mobileNavOpen = false" />
    </Transition>

    <!-- Main Content Area -->
    <div class="dashboard-layout__content">
      <!-- Top Header -->
      <header class="dashboard-header">
        <!-- DEV-2232: единственный способ открыть навигацию на телефоне -->
        <button
          type="button"
          class="header-burger"
          :aria-label="t('common.actions.openMenu')"
          :aria-expanded="mobileNavOpen"
          @click="mobileNavOpen = !mobileNavOpen"
        >
          <MenuIcon :size="22" />
        </button>

        <div class="header-breadcrumbs">
          <nav aria-label="Breadcrumb">
            <ol class="breadcrumb">
              <template v-if="breadcrumbItems.length">
                <li
                  v-for="(item, index) in breadcrumbItems"
                  :key="index"
                  class="breadcrumb-item"
                  :class="{ active: index === breadcrumbItems.length - 1 }"
                >
                  <router-link v-if="item.to && index < breadcrumbItems.length - 1" :to="item.to">
                    {{ item.label }}
                  </router-link>
                  <span v-else>{{ item.label }}</span>
                </li>
              </template>
              <template v-else>
                <li class="breadcrumb-item">
                  <router-link to="/dashboard">{{ $t('common.navigation.home') }}</router-link>
                </li>
                <li v-if="currentPageTitle" class="breadcrumb-item active">
                  {{ currentPageTitle }}
                </li>
              </template>
            </ol>
          </nav>
        </div>

        <div class="header-actions">
          <!-- Global Search (DEV-303): инлайн-поле с дропдауном подсказок -->
          <GlobalSearch ref="globalSearchRef" />

          <div class="header-actions__group">
            <!-- SIP status (DEV-425: перенесён из плавающего бейджа в топбар) -->
            <SipStatusIndicator />

            <!-- Notifications Bell -->
            <button
              class="header-iconbtn header-iconbtn--bell"
              type="button"
              @click="handleNotificationsClick"
            >
              <BellIcon :size="20" />
              <span v-if="notificationCount > 0" class="header-iconbtn__badge">
                {{ notificationCount > 99 ? '99+' : notificationCount }}
              </span>
            </button>

            <!-- User Menu -->
            <div ref="userMenuRef" class="header-user">
              <button class="header-user__trigger" type="button" @click="toggleUserMenu">
                <span class="header-user__avatar">
                  <img
                    v-if="userAvatarUrl && !avatarError"
                    :src="userAvatarUrl"
                    alt=""
                    class="header-user__avatar-img"
                    @error="avatarError = true"
                  />
                  <template v-else>{{ getUserInitials() }}</template>
                </span>
                <span class="header-user__meta">
                  <span class="header-user__name">{{ currentUser.name }}</span>
                  <span class="header-user__role">{{ userRoleLabel }}</span>
                </span>
              </button>
              <Transition name="fade">
                <div v-if="userMenuOpen" class="user-menu-dropdown">
                  <router-link
                    to="/dashboard/profile"
                    class="user-menu-item"
                    @click="userMenuOpen = false"
                  >
                    <UserIcon :size="16" />
                    {{ $t('profile.title') }}
                  </router-link>
                  <button class="user-menu-item user-menu-item--danger" @click="handleLogout">
                    <LogOutIcon :size="16" />
                    {{ impersonating ? $t('impersonation.endMenu') : $t('auth.logout') }}
                  </button>
                </div>
              </Transition>
            </div>
          </div>
        </div>
      </header>

      <!-- Page Content -->
      <main class="dashboard-main">
        <router-view v-slot="{ Component }">
          <transition name="fade-slide" mode="out-in">
            <!-- key по path (без query): переход между карточками одного типа
                 (/clients/A → /clients/B) меняет только params, компонент
                 переиспользуется и onMounted не срабатывает — карточка залипала
                 на данных предыдущей сущности (в т.ч. её сделках). Query не в
                 ключе: смена вкладки (?tab=) не должна перезагружать страницу. -->
            <component :is="Component" :key="$route.path" />
          </transition>
        </router-view>
      </main>
    </div>

    <!-- SIP Call Widget. Во вкладке просмотра не монтируется: он регистрирует SIP
         от имени пользователя, и его звонки пошли бы в браузер администратора (DEV-2399). -->
    <ActiveCallWidget v-if="!impersonating" />

    <!-- Notification Panel -->
    <NotificationPanel />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, provide, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import AppSidebar from '@/components/AppSidebar.vue'
import { useReferences } from '@/features/references'
import { useAuth } from '@/features/auth'
import { useAuthStore } from '@/features/auth/stores/auth.store'
import { getPrimaryRole } from '@/features/auth/types/auth.types'
import { getCdnUrl } from '@/api/config'
import { useCallEvents } from '@/features/telephony/composables/useCallEvents'
import ActiveCallWidget from '@/features/telephony/components/ActiveCallWidget.vue'
import SipStatusIndicator from '@/features/telephony/components/SipStatusIndicator.vue'
import NotificationPanel from '@/features/notifications/components/NotificationPanel.vue'
import { useNotifications } from '@/features/notifications'
import GlobalSearch from '@/features/search/components/GlobalSearch.vue'
import { useListReturnNav } from '@/composables/useListReturnNav'
import { onClickOutside } from '@vueuse/core'
// Icons
import { BellIcon, UserIcon, LogOutIcon, MenuIcon } from 'lucide-vue-next'

// Composables
const { loadAll: loadReferences } = useReferences()
const { logout, user } = useAuth()
const authStore = useAuthStore()
// Вкладка просмотра «глазами пользователя» (DEV-2399).
const impersonating = authStore.impersonation !== null
const { t } = useI18n()

// Аватар пользователя для топбара: полный URL из user store (после загрузки аватара)
// или из настроек профиля (avatar_url → CDN); иначе кружок с инициалами (DEV-976).
const userAvatarUrl = computed(() => {
  if (user.value?.avatar) return user.value.avatar
  const path = authStore.userSettings?.avatar_url
  return path ? getCdnUrl(path) : null
})
// Битая/удалённая картинка → фолбэк на инициалы; сброс при смене URL.
const avatarError = ref(false)
watch(userAvatarUrl, () => {
  avatarError.value = false
})
const router = useRouter()
const callEvents = useCallEvents()
const {
  unreadCount: notificationCount,
  togglePanel,
  startPolling,
  stopPolling,
} = useNotifications()

provide('callEvents', callEvents)

// State
const route = useRoute()

// DEV-959: хлебные крошки «Клиенты»/«Сделки»/«Агенты» ведут в список с сохранёнными
// фильтрами (запомненный URL), а не на голый путь.
const clientsReturnNav = useListReturnNav('clients')
const dealsReturnNav = useListReturnNav('deals')
const agentsReturnNav = useListReturnNav('agents')
const propertiesReturnNav = useListReturnNav('properties')
const videomeetingsReturnNav = useListReturnNav('videomeetings')
const listReturnPath: Record<string, () => string> = {
  '/dashboard/clients': () => clientsReturnNav.resolveListPath('/dashboard/clients'),
  '/dashboard/deals': () => dealsReturnNav.resolveListPath('/dashboard/deals'),
  '/dashboard/agents': () => agentsReturnNav.resolveListPath('/dashboard/agents'),
  '/dashboard/properties': () => propertiesReturnNav.resolveListPath('/dashboard/properties'),
  '/dashboard/videomeetings': () =>
    videomeetingsReturnNav.resolveListPath('/dashboard/videomeetings'),
}

const sidebarCollapsed = ref(false)
// DEV-2232: выехавшая навигация на <900px. Закрывается тапом по бэкдропу,
// Escape и любым переходом — иначе после выбора пункта она перекрывает страницу.
const mobileNavOpen = ref(false)
const dynamicBreadcrumbData = ref<Record<string, string>>({})
const userMenuOpen = ref(false)
const userMenuRef = ref<HTMLElement | null>(null)
const globalSearchRef = ref<InstanceType<typeof GlobalSearch> | null>(null)

onClickOutside(userMenuRef, () => {
  userMenuOpen.value = false
})

// User data из auth store
const currentUser = computed(
  () =>
    user.value || {
      name: 'User',
      role: 'Guest',
      email: '',
    },
)

// Локализованная роль: берём старшую роль, а не первую из user.roles (DEV-196).
const userRoleLabel = computed(() => {
  const u = user.value
  const roles = u && 'roles' in u ? u.roles : undefined
  const roleKey = getPrimaryRole(roles) || 'user'
  return t(`auth.roles.${roleKey}`)
})

// Provide for child components to update dynamic breadcrumbs
provide('updateBreadcrumb', (key: string, value: string) => {
  dynamicBreadcrumbData.value[key] = value
})

// Cmd/Ctrl+K — фокус на инлайн-поле поиска в топбаре (DEV-303)
const handleSearchHotkey = (e: KeyboardEvent) => {
  if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
    e.preventDefault()
    globalSearchRef.value?.focus()
  }
  if (e.key === 'Escape') mobileNavOpen.value = false
}

watch(
  () => route.fullPath,
  () => {
    mobileNavOpen.value = false
  },
)

// Lifecycle - загрузка справочников и запуск polling уведомлений
onMounted(async () => {
  window.addEventListener('keydown', handleSearchHotkey)
  // Отказ справочника стор ловит и логирует сам (DEV-1801) — здесь ловить нечего.
  await loadReferences()
  startPolling()
})

onUnmounted(() => {
  stopPolling()
  window.removeEventListener('keydown', handleSearchHotkey)
})

// Helper function to translate breadcrumb labels
const translateBreadcrumb = (label: string): string => {
  if (!label) return ''

  // Handle dynamic breadcrumbs
  if (label.startsWith(':')) {
    const key = label.slice(1)
    return dynamicBreadcrumbData.value[key] || label
  }

  // Handle i18n keys
  let translationKey = label

  // Format 1: i18n:key.path - REMOVE prefix
  if (label.startsWith('i18n:')) {
    translationKey = label.slice(5)
    return t(translationKey)
  }
  // Format 2: key.path (looks like i18n key)
  else if (label.includes('.') && !label.includes(' ')) {
    const translated = t(label)
    return translated !== label ? translated : label
  }

  // Not an i18n key, return as is
  return label
}

// Computed
const breadcrumbItems = computed(() => {
  const meta = route.meta?.breadcrumbs as Array<{ label: string; to?: string; dynamic?: boolean }>
  if (!meta || !Array.isArray(meta)) return []

  return meta.map((item) => {
    const resolver = item.to ? listReturnPath[item.to] : undefined
    return {
      ...item,
      to: resolver ? resolver() : item.to,
      label: translateBreadcrumb(item.label),
    }
  })
})

const currentPageTitle = computed(() => {
  // Можно настроить маппинг роутов на заголовки
  const titles: Record<string, string> = {
    '/dashboard/clients': 'Клиенты',
    '/dashboard/deals': 'Сделки',
    '/dashboard/properties': 'Проекты',
    '/dashboard/settings': 'Настройки',
    '/dashboard/reports': 'Отчеты',
    '/dashboard/calls': 'Звонки',
    '/dashboard/documents': 'Документы',
  }

  return titles[route.path] || ''
})

// Methods
const getUserInitials = () => {
  const name = currentUser.value?.name || 'User'
  const names = name.split(' ').filter((n) => n.length > 0)
  if (names.length >= 2 && names[0] && names[1]) {
    return `${names[0][0]}${names[1][0]}`.toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
}

const handleLogout = async () => {
  // Во вкладке просмотра «выйти» — закончить просмотр, а не сеанс пользователя.
  if (impersonating) {
    await authStore.endImpersonation()
    return
  }

  try {
    await logout()
    router.push('/login')
  } catch (error) {
    console.error('Logout error:', error)
  }
}

const handleNotificationsClick = () => {
  togglePanel()
}

const toggleUserMenu = () => {
  userMenuOpen.value = !userMenuOpen.value
}
</script>

<style scoped>
.dashboard-layout {
  /* DEV-425: высота хедера вынесена в переменную — её вычитают full-height экраны
     (например канбан сделок), чтобы не плодить «магические» 64px по месту. */
  --app-header-height: 64px;
  display: flex;
  min-height: 100vh;
  background: var(--color-background-secondary);
}

/* Content Area */
.dashboard-layout__content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

/* Header */
.dashboard-header {
  height: var(--app-header-height);
  background: var(--color-background-elevated);
  border-bottom: 1px solid var(--color-border-light);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1.5rem;
  position: sticky;
  /* DEV-2399: под плашкой вкладки просмотра, если она есть */
  top: var(--impersonation-banner-height, 0px);
  z-index: var(--z-index-header, 30);
}

.header-breadcrumbs {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

/* DEV-2232: бургер виден только на мобильном брейкпоинте */
.header-burger {
  display: none;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  margin-left: -10px;
  border: none;
  border-radius: var(--radius-md, 10px);
  background: transparent;
  color: var(--color-text-secondary);
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.header-burger:hover {
  background: var(--color-bg-muted);
  color: var(--color-text-primary);
}

/* DEV-2232: затемнение под выехавшей навигацией; сайдбар выше него.
   DEV-2235: слой из шкалы ДС — затемнение строго между шапкой и сайдбаром. */
.dashboard-layout__backdrop {
  position: fixed;
  inset: 0;
  z-index: var(--z-index-backdrop, 35);
  background: rgba(0, 0, 0, 0.45);
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  list-style: none;
  margin: 0;
  padding: 0;
  flex-wrap: nowrap;
  min-width: 0;
  overflow: hidden;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  min-width: 0;
  white-space: nowrap;
}

/* DEV-2233: последний уровень сжимается многоточием, а не переносит строку */
.breadcrumb-item:last-child,
.breadcrumb-item:last-child span,
.breadcrumb-item:last-child a {
  overflow: hidden;
  text-overflow: ellipsis;
}

.breadcrumb-item:not(:last-child)::after {
  content: '/';
  color: var(--color-text-tertiary);
  margin-left: 0.5rem;
}

.breadcrumb-item a {
  color: var(--color-text-secondary);
  text-decoration: none;
  transition: color 0.2s;
}

.breadcrumb-item a:hover {
  color: var(--color-primary);
}

.breadcrumb-item.active {
  color: var(--color-text-primary);
  font-weight: 500;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 14px;
}

.header-actions__group {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Icon button (bell) */
.header-iconbtn {
  position: relative;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: var(--radius-md, 10px);
  background: transparent;
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease;
}

.header-iconbtn:hover {
  background: var(--color-bg-muted);
  color: var(--color-text-primary);
}

.header-iconbtn__badge {
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: var(--color-danger);
  color: #fff;
  font-size: 0.625rem;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
  border-radius: 9px;
  pointer-events: none;
}

/* User chip */
.header-user {
  position: relative;
}

.header-user__trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 6px 4px 4px;
  border: none;
  background: transparent;
  border-radius: var(--radius-md, 10px);
  cursor: pointer;
  transition: background 0.15s ease;
}

.header-user__trigger:hover {
  background: var(--color-bg-muted);
}

.header-user__avatar {
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: var(--radius-full, 9999px);
  background: var(--color-primary);
  color: var(--color-on-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem; /* 13px */
  font-weight: 600;
  overflow: hidden;
}

.header-user__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.header-user__meta {
  display: flex;
  flex-direction: column;
  gap: 1px;
  align-items: flex-start;
  min-width: 0;
}

.header-user__name {
  font-size: 0.84375rem; /* 13.5px */
  font-weight: 600;
  color: var(--color-text-primary);
  white-space: nowrap;
}

.header-user__role {
  font-size: 0.75rem; /* 12px */
  color: var(--color-text-muted);
  white-space: nowrap;
}

/* User Menu Dropdown */
.user-menu-dropdown {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  min-width: 180px;
  background: var(--color-background-elevated, #fff);
  border: 1px solid var(--color-border-light, #e2e8f0);
  border-radius: 0.5rem;
  box-shadow: var(--shadow-lg, 0 10px 15px -3px rgba(0, 0, 0, 0.1));
  padding: 0.25rem;
  z-index: 50;
}

.user-menu-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.625rem 0.75rem;
  border: none;
  background: transparent;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: var(--color-text-primary, #1a1a2e);
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s;
}

.user-menu-item:hover {
  background: var(--color-background-secondary, #f1f5f9);
}

.user-menu-item--danger {
  color: var(--color-danger, #ef4444);
}

.user-menu-item--danger:hover {
  background: var(--color-danger-tint);
}

/* Main Content */
.dashboard-main {
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
}

/* Animations */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Responsive */
@media (max-width: 860px) {
  .header-user__meta {
    display: none;
  }
}

/* DEV-2232: ниже 900px сайдбар уезжает за экран и открывается бургером —
   при его ширине 280px во flex-ряду контенту на 390px оставалось 110px. */
@media (max-width: 900px) {
  .header-burger {
    display: flex;
  }
}

@media (max-width: 768px) {
  .dashboard-header {
    padding: 0 1rem;
    gap: 0.5rem;
  }

  .dashboard-main {
    padding: 1rem;
  }
}

/* DEV-2242: колокол и профиль — полноценные тап-цели на тач-экране */
@media (pointer: coarse) {
  .header-iconbtn {
    width: 44px;
    height: 44px;
  }

  .header-user__trigger {
    min-height: 44px;
  }
}

/* DEV-2233: на телефоне в крошках остаётся текущий уровень — на полный путь
   в одной строке с бургером и иконками места нет. */
@media (max-width: 600px) {
  .breadcrumb-item:not(:last-child) {
    display: none;
  }

  .header-actions {
    gap: 6px;
  }

  .header-actions__group {
    gap: 2px;
  }
}
</style>
